<!-- AppTopBar.vue-->
<template>
  <v-app-bar flat class="cl-bar">
    <v-img :src="logoURL" class="mr-3 cl-bar-logo" role="button"
          tabindex="0" alt="Home" @click="goHome" @keyup.enter="goHome"/>
    <v-toolbar-title class="cl-bar-title">Match Reports</v-toolbar-title>
    <v-spacer />
    <div class="cl-userinfo"><strong>{{ org }}</strong> ({{ role }}) — {{ nameFirst }} {{ nameLast }}</div>
    
    <v-toolbar-items>
      <v-btn v-for="(item, idx) in menu" :key="item.to || idx" variant="text" :to="item.to" class="text-white ">{{ item.label }}</v-btn>
      <v-btn @click="openProfileDialog" :key="'profile-btn'" prepend-icon="mdi-account-circle" variant="text" class="text-white">Profile</v-btn>
      <v-btn @click="logout" :key="'logout-btn'" variant="text" class="text-white" prepend-icon="mdi-logout">Logout</v-btn>
    </v-toolbar-items>
  </v-app-bar>

  <!-- Profile dialog -->
  <v-dialog v-if="mounted" v-model="profileDialogOpen" max-width="640" :retain-focus="false" class="profile-dialog">
    <v-card>
      <v-card-title class="d-flex align-center">
        <span class="text-h6">Referee Account</span>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" @click="closeProfileDialog" />
      </v-card-title>

      <v-card-text>
        <!-- ⬅️ Note shown when opened from DashboardReferee -->
        <v-alert v-if="dialogNote" type="info" variant="tonal" class="mb-3">{{ dialogNote }}</v-alert>
        <v-form>
          <v-text-field label="Organization" v-model="profile.Organization" readonly />
          <v-text-field
            label="First Name"
            v-model="profile.NameFirst"
            :readonly="!isEditing"
            required
            ref="firstNameRef"
            :autofocus="isEditing"
          />
          <v-text-field label="Last Name" v-model="profile.NameLast" :readonly="!isEditing" required />
          <v-text-field label="Name in ArbiterSports" v-model="profile.NameInArbiterSports" :readonly="!isEditing" />
          <v-text-field label="Email" v-model="profile.Email" readonly />
          <v-text-field label="Phone" v-model="profile.Phone" :readonly="!isEditing" />
          <v-text-field label="USSF ID" v-model="profile.USSFID" :readonly="!isEditing" />

          <v-alert v-if="errorMessage" type="error" variant="outlined" class="mt-2">{{ errorMessage }}</v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" :loading="isLoading" @click="saveAndClose">
          {{ isEditing ? "Save Profile" : "Edit Profile" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Loading overlay (below dialog) -->
  <v-overlay v-if="mounted" :model-value="loading" class="loading-overlay align-center justify-center" persistent>
    <div class="d-flex flex-column align-center ga-2">
      <v-progress-circular indeterminate size="40" />
      <div>Loading…</div>
    </div>
  </v-overlay>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { getAuth, signOut, getIdTokenResult } from "firebase/auth";
import { db } from "@/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useInactivityLogout } from "@/composables/useInactivityLogout";
import logoURL from "@/assets/MN logo 40px-40px.png";

const mounted = ref(false);

/* ---------- role/org/name from localStorage (safe values only) ---------- */
const org       = ref(localStorage.getItem("currentUserOrgName")   || "");
const role      = ref(localStorage.getItem("currentUserRole")      || "");
const nameFirst = ref(localStorage.getItem("currentUserNameFirst") || "");
const nameLast  = ref(localStorage.getItem("currentUserNameLast")  || "");

/* ---------- role-based menu ---------- */
const MENU_BY_ROLE = {
  Referee: [
    { label: "My Match Reports", to: "/dashboard-referee" },
    { label: "My Misconduct Reports", to: "/misconduct-referee" },
    { label: "Help", to: "/help" },
  ],
  RefereeCoach: [
    { label: "Coach Dashboard", to: "/dashboard-refcoach" },
    { label: "Misconduct", to: "/misconduct-referee" },
    { label: "Help", to: "/help" },
  ],
  ADMIN: [
    { label: "Match Reports", to: "/dashboard-admin" },
    { label: "Misconduct", to: "/misconduct-admin" },
    { label: "Leagues", to: "/maintain-leagues" },
    { label: "GenderAges", to: "/maintain-genderages" },
    { label: "Game Schedule", to:"/maintain-gameschedule" },
    { label: "MisconductCodes", to:"/maintain-misconductcodes" },
    { label: "Metrics", to: "/metrics" },
    { label: "Users", to: "/maintain-users" },
    { label: "Help", to: "/help" },
  ],
};

function normalizeRole(r) {
  const v = (r || "").trim()
  return v === "ADMIN" || v === "RefereeCoach" || v === "Referee" ? v : "Referee"
}

const menu = computed(() => MENU_BY_ROLE[normalizeRole(role.value)])

/* ---------- router/auth ---------- */
const router = useRouter();
const route = useRoute();
const auth = getAuth();

/* ---------- dialog & form state ---------- */
const profileDialogOpen = ref(false);
const isEditing = ref(true);
const loading = ref(false);
const isLoading = ref(false);
const errorMessage = ref("");
const dialogNote = ref(""); 
const firstNameRef = ref(null);

const profile = ref({
  Organization: "",
  Email: "",
  NameFirst: "",
  NameLast: "",
  NameInArbiterSports: "",
  Phone: "",
  USSFID: "",
});

onMounted(async() => {
  mounted.value = true;
  window.addEventListener("open-profile", handleOpenProfile);
  window.addEventListener("storage", syncFromStorage);
  
  // Refresh claims once on mount so the correct menu shows up immediately
  try {
    const user = auth.currentUser;
    if (user) {
      const { claims } = await getIdTokenResult(user, true);
      // 🔧 FIX: Use localStorage role first
         const storedRole = localStorage.getItem("currentUserRole");
         console.log(`📝 AppTopBar onMount - Stored: ${storedRole}, Claims: ${claims?.role}, Current: ${role.value}`);
         const cRole = normalizeRole(storedRole || role.value || claims?.role || (Array.isArray(claims?.roles) ? claims.roles[0] : ""));
      role.value = cRole;
      setSafeLocalStorage(org.value, cRole, nameFirst.value, nameLast.value);

      // If we're on any dashboard and it's not the one for this role, move there now
      const expected = dashboardPathFor(cRole);
      const onDashboard =
        route.path.startsWith("/dashboard-") || route.name?.toString().toLowerCase().includes("dashboard");
      if (onDashboard && route.path !== expected) {
        router.replace(expected);
      }

      // Auto-trigger profile dialog if name is missing
      if (!nameFirst.value?.trim() || !nameLast.value?.trim()) {
        dialogNote.value = "Please complete your profile information to get started.";
        await openProfileDialog();
      }
    }
  } catch (e) {
    // non-fatal; menu will still show based on existing localStorage
    console.debug("claim refresh skipped:", e?.message || e);
  }
});

onUnmounted(() => {
  window.removeEventListener("open-profile", handleOpenProfile);
  window.removeEventListener("storage", syncFromStorage);
});

/* ---------- localStorage sync helpers ---------- */
function syncFromStorage() {
  org.value       = localStorage.getItem("currentUserOrgName")   || "";
  role.value      = localStorage.getItem("currentUserRole")      || "";
  nameFirst.value = localStorage.getItem("currentUserNameFirst") || "";
  nameLast.value  = localStorage.getItem("currentUserNameLast")  || "";
}
function setSafeLocalStorage(sOrg, sRole, fName, lName) {
  try {
    if (sOrg !== undefined)  localStorage.setItem("currentUserOrgName", sOrg || "");
    if (sRole !== undefined) localStorage.setItem("currentUserRole", sRole || "");
    if (fName !== undefined) localStorage.setItem("currentUserNameFirst", fName || "");
    if (lName !== undefined) localStorage.setItem("currentUserNameLast", lName || "");
  } catch {}
}
function clearSafeLocalStorage() {
  try {
    localStorage.removeItem("currentUserOrgName");
    localStorage.removeItem("currentUserRole");
    localStorage.removeItem("currentUserNameFirst");
    localStorage.removeItem("currentUserNameLast");
    localStorage.removeItem("lastActiveAt");
  } catch {}
}

function dashboardPathFor(r) {
  const rr = normalizeRole(r);
  if (rr === "ADMIN") return "/dashboard-admin";
  if (rr === "RefereeCoach") return "/dashboard-refcoach";
  return "/dashboard-referee";
}

/* ---------- profile loading ---------- */
async function loadProfile() {
  const user = auth.currentUser;
  if (!user) {
    try { router.push("/login"); } catch {}
    throw new Error("Not signed in");
  }

  // refresh claims to keep role/org fresh
  const { claims } = await getIdTokenResult(user, true);
  // 🔧 FIX: Use localStorage role first, then fallback to claims
     const storedRole = localStorage.getItem("currentUserRole");
     const storedOrg = localStorage.getItem("currentUserOrgName");
     console.log(`📝 AppTopBar loadProfile - Stored: ${storedRole}/${storedOrg}, Claims role: ${claims?.role}`);
     const cRole = storedRole || claims?.role || (Array.isArray(claims?.roles) ? claims.roles[0] : "");
  const cOrg  = storedOrg || claims?.org || "";

  const userRef = doc(db, "users", user.uid);
  const snap = await getDoc(userRef);
  const userDoc = snap.exists() ? snap.data() : {};

  profile.value.Email = userDoc.email || user.email || "";
  // 🔧 FIX: Use stored org first, not first in Roles array
    profile.value.Organization = cOrg || userDoc.Roles?.[0]?.Organization || "";
  profile.value.NameFirst = userDoc.NameFirst || "";
  profile.value.NameLast  = userDoc.NameLast  || "";
  profile.value.NameInArbiterSports = userDoc.NameInArbiterSports || "";
  profile.value.Phone = userDoc.Phone || "";
  profile.value.USSFID = userDoc.USSFID || "";

  // hydrate top bar immediately
  // 🔧 FIX: Use stored org if available
     org.value = cOrg || profile.value.Organization;
  role.value = cRole || role.value;
  nameFirst.value = profile.value.NameFirst;
  nameLast.value = profile.value.NameLast;
  setSafeLocalStorage(org.value, role.value, profile.value.NameFirst, profile.value.NameLast);
}

/* ---------- open/close profile dialog ---------- */
async function openProfileDialog() {
  if (profileDialogOpen.value || loading.value) return; // already opening/open
  loading.value = true;
  errorMessage.value = "";
  isEditing.value = true;

  try {
    await loadProfile();
  } catch (err) {
    console.error("loadProfile error:", err);
    errorMessage.value = "Error loading your profile.";
  } finally {
    loading.value = false;           // close overlay first
    await nextTick();                // then open dialog on top
    profileDialogOpen.value = true;
    await nextTick();
    try { firstNameRef.value?.focus?.(); } catch {}
  }
}
function closeProfileDialog() {
  profileDialogOpen.value = false;
  dialogNote.value = "";
}

/* Listen for external open request (from DashboardReferee, etc.) */
function handleOpenProfile(ev) {
  dialogNote.value = ev?.detail?.note || "";
  openProfileDialog();
}

/* ---------- save ---------- */
async function saveAndClose() {
  if (!isEditing.value) {
    isEditing.value = true;
    await nextTick();
    try { firstNameRef.value?.focus?.(); } catch {}
    return;
  }

  const user = auth.currentUser;
  if (!user) return;

  isLoading.value = true;
  errorMessage.value = "";
  try {
    const userRef = doc(db, "users", user.uid);
    const update = {
      NameFirst: profile.value.NameFirst,
      NameLast: profile.value.NameLast,
      NameInArbiterSports: profile.value.NameInArbiterSports,
      Phone: profile.value.Phone,
      USSFID: profile.value.USSFID,
    };
    await updateDoc(userRef, update);

    // update header immediately (same tab) + localStorage
    nameFirst.value = profile.value.NameFirst || "";
    nameLast.value  = profile.value.NameLast  || "";
    org.value       = profile.value.Organization || org.value;
    setSafeLocalStorage(org.value, role.value, nameFirst.value, nameLast.value);

    isEditing.value = false;
    profileDialogOpen.value = false;
    dialogNote.value = "";
  } catch (err) {
    console.error("Error updating profile:", err);
    errorMessage.value = "Error updating profile.";
  } finally {
    isLoading.value = false;
  }
}

/* ---------- auto-redirect when role changes ---------- */
watch(role, (newRole) => {
  const expected = dashboardPathFor(newRole);
  // Only auto-redirect if we're currently on a dashboard* route and it's not the expected one
  const onDashboard =
    route.path.startsWith("/dashboard-") || route.name?.toString().toLowerCase().includes("dashboard");
  if (onDashboard && route.path !== expected) {
    router.replace(expected);
  }
});

/* ---------- logout (manual + inactivity) ---------- */
async function logout() {
  try { await signOut(getAuth()); } catch (e) { console.error("signOut error", e); }
  finally {
    clearSafeLocalStorage();
    try { router.push("/login"); } catch {}
  }
}
useInactivityLogout({
  timeoutMs: 20 * 60 * 1000, // 20 minutes
  onTimeout: logout,
});
function goHome() {
  const user = auth.currentUser
  // if not signed in, send to login
  if (!user) return router.push('/login')

  // use your existing role → dashboard resolver
  const dest = dashboardPathFor(role.value)
  router.push(dest)
}
</script>

<style scoped>
.text-white { color: #fff !important; }
.cl-bar {
  position: fixed; display: flex;
  top: 0; left: 0; height: 80px; width: 100%; z-index: 1000;
  margin: 0; padding: 0;
  background-color: #ae431e !important; color: white !important;
  justify-content: space-between;
  align-items: center;
}
.cl-bar-logo {
  position: fixed;
  top: 10px; left: 15px;
  width: 50px; height: auto;
  cursor: pointer;
}
.cl-bar-title {
  position: fixed;
  top: 15px; left: 60px;
  font-family: Arial, sans-serif;
  font-size: xx-large; font-weight: bold;
}
.cl-userinfo {
  position: fixed;
  top: 45px; left: 100px;
  font-family: 'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif;
  font-size: 16pt;
  color: #f8e5e5;
}
/* Ensure dialog stacks above loader; moved from props to CSS */
:deep(.profile-dialog) {
  z-index: 3000 !important;
}
:deep(.profile-dialog .v-overlay__scrim) {
  background-color: rgba(0, 0, 0, 0.28) !important;
}
:deep(.loading-overlay) {
  z-index: 1500 !important;
}
:deep(.loading-overlay .v-overlay__scrim) {
  background-color: rgba(0, 0, 0, 0.12) !important;
}
</style>